import {
    getTpdAdvanceAmountCents,
    sumEligibleSliceAmountCentsInPeriod,
    type SliceWithTpdAdvanceEligibility,
    type TpdLike,
} from "./advanceSliceAmounts";
import type { TPeriod } from "store/periodStore";

export interface TermAdvancePreviewTotals {
    /** Total standalone advances in period already reflected on the TPD. */
    currentTotalCents: number;
    /** Projected total after applying form changes, including other payments on the same term. */
    previewTotalCents: number;
}

export interface TpdAdvanceImpactResult {
    grossDuty: number;
    nextBalance: number;
}

/**
 * Mirrors `computeOneTermCalculations` advancePaidInPeriod block in the backend.
 * @see crealo-backend/packages/crealobackend/src/utils/calculation.ts
 */
export function getGrossDutyBeforeAdvance(tpd: TpdLike): number {
    return (tpd?.grossDuty ?? 0) + getTpdAdvanceAmountCents(tpd);
}

/**
 * Closing balance before the advance excess is applied to nextBalance.
 */
export function getBalanceBeforeAdvanceImpact(tpd: TpdLike): number {
    const grossBeforeAdvance = getGrossDutyBeforeAdvance(tpd);
    const advanceInPeriod = getTpdAdvanceAmountCents(tpd);
    const advanceExcess = Math.min(0, grossBeforeAdvance - advanceInPeriod);
    return (tpd?.nextBalance ?? 0) - advanceExcess;
}

/**
 * Recomputes grossDuty and nextBalance after applying standalone advances paid in the period.
 * Only slices counted by `getTotalAdvancesInPeriod` should be included in advancePaidInPeriodCents.
 */
export function applyAdvancePaidInPeriodImpact(tpd: TpdLike, advancePaidInPeriodCents: number): TpdAdvanceImpactResult {
    const grossBeforeAdvance = getGrossDutyBeforeAdvance(tpd);
    const balanceBeforeAdvance = getBalanceBeforeAdvanceImpact(tpd);

    if (advancePaidInPeriodCents <= 0) {
        return {
            grossDuty: grossBeforeAdvance,
            nextBalance: balanceBeforeAdvance,
        };
    }

    const grossAfterAdvance = grossBeforeAdvance - advancePaidInPeriodCents;
    if (grossAfterAdvance >= 0) {
        return {
            grossDuty: grossAfterAdvance,
            nextBalance: balanceBeforeAdvance,
        };
    }

    return {
        grossDuty: 0,
        nextBalance: balanceBeforeAdvance + grossAfterAdvance,
    };
}

/**
 * Builds current/preview advance totals for the management table preview while editing.
 * Applies only the delta between the initial form snapshot and the current form values
 * on top of the advance total already stored on the TPD.
 */
export function resolveTermAdvancePreviewTotals(
    tpd: TpdLike,
    initialFormSlices: SliceWithTpdAdvanceEligibility[],
    watchedFormSlices: SliceWithTpdAdvanceEligibility[],
    period: Pick<TPeriod, "periodStart" | "periodEnd">,
    termAdvanceAmountFallback = 0,
): TermAdvancePreviewTotals {
    const { periodStart, periodEnd } = period;
    const managementAdvanceTotalCents = tpd ? getTpdAdvanceAmountCents(tpd) : termAdvanceAmountFallback;
    const initialFormInPeriodCents = sumEligibleSliceAmountCentsInPeriod(initialFormSlices, periodStart, periodEnd);
    const previewFormInPeriodCents = sumEligibleSliceAmountCentsInPeriod(watchedFormSlices, periodStart, periodEnd);
    return {
        currentTotalCents: managementAdvanceTotalCents,
        previewTotalCents: managementAdvanceTotalCents - initialFormInPeriodCents + previewFormInPeriodCents,
    };
}
